/* eslint-disable max-len */

import { clone, debounce } from 'lodash-es';
import { mapState, mapGetters, mapActions } from 'vuex';
import EventBus from '../utils/event-bus';
import toast from '../utils/toasts';

import HeaderComponent from '../components/Header.vue';
import AlertComponent from '../components/Alert.vue';
import ModalComponent from '../components/Modal/Modal.vue';
import SidebarLeftComponent from '../components/SidebarLeft/SidebarLeft.vue';
import SidebarRightComponent from '../components/SidebarRight.vue';
import TemplatePreviewComponent from '../components/TemplatePreview.vue';

export default {
  components: {
    alert: AlertComponent,
    'header-bar': HeaderComponent,
    modal: ModalComponent,
    'template-preview': TemplatePreviewComponent,
    'sidebar-left': SidebarLeftComponent,
    'sidebar-right': SidebarRightComponent,
  },
  data() {
    return {
      loaded: false,
      original: null,
      showAlert: false,
      assetUploaderOpen: false,
      assetUploaderType: 'image',
      keyboardShortcutsOpen: false,
      undoStack: [],
      redoStack: [],
      previousSnapshot: null,
      clipboard: [],
    };
  },
  mounted() {
    // TODO - refactor away from jQuery
    $(document).on('keydown', (event) => {
      if (this.textEditing || $(event.target).is('input') || $(event.target).is('textarea')) {
        return;
      }

      const ctrlShiftPressed = (event.metaKey && event.shiftKey) || (event.ctrlKey && event.shiftKey);
      const ctrlPressed = event.metaKey || event.ctrlKey;

      if (ctrlShiftPressed && event.keyCode === 71) { // Command + Shift + G
        event.preventDefault();
        this.unGroupSelectedGroups();
        return;
      }

      if (ctrlPressed && event.keyCode === 71) { // Command + G
        event.preventDefault();
        this.groupSelectedElements();
        return;
      }

      if (ctrlPressed && event.keyCode === 83) { // Command + S
        event.preventDefault();
        this.save();
        return;
      }

      if (ctrlPressed && event.keyCode === 186) { // Command + ;
        this.$store.commit('TOGGLE_SHOW_SAFEAREA');
        return;
      }

      if (ctrlPressed && event.keyCode === 67) { // Command + C
        this.copyToClipboard();
        return;
      }

      if (ctrlPressed && event.keyCode === 86) { // Command + V
        this.pasteFromClipboard();
        return;
      }

      if (ctrlPressed && event.key === 'd') {
        event.preventDefault();
        this.copyToClipboard();
        this.pasteFromClipboard();
        return;
      }

      if (ctrlShiftPressed && event.keyCode === 90) { // Shift + Command + Z
        this.redo();
        return;
      }

      if (ctrlPressed && event.keyCode === 90) { // Command + Z
        this.undo();
        return;
      }

      if (event.key === 'Escape' && this.keyboardShortcutsOpen) this.keyboardShortcutsOpen = false;

      switch (event.which) {
        case 8: // Backspace
        case 46: // Delete
          if (this.selectedElements.length) {
            this.deleteSelectedElements().then(() => this.$store.dispatch('setTextEditing', false));
          }
          break;
        case 37:
        case 38:
        case 39:
        case 40:
          if (this.selectedElements.length) {
            EventBus.emit('keyup-arrow', event);
          }
          break;
        default:
      }
    });

    document.addEventListener('click', this.handleClickOutside);
  },

  beforeUnmount() {
    $(document).off('keydown');
    $(document).off('keyup');
    document.removeEventListener('click', this.handleClickOutside);
  },

  methods: {
    ...mapActions('graphicsTemplate', ['groupSelectedElements', 'unGroupSelectedGroups', 'deleteSelectedElements', 'duplicateElement']),
    handleClickOutside(e) {
      if (e.target.id.includes('keyboard-shortcuts-modal') && this.keyboardShortcutsOpen) this.keyboardShortcutsOpen = false;
    },
    toggleAssetModal(opts) {
      this.assetUploaderType = opts.type;
      this.assetUploaderOpen = !this.assetUploaderOpen;
    },
    togglekeyboardShortcuts(bool) {
      this.keyboardShortcutsOpen = bool;
    },
    copyToClipboard() {
      const containsRequiredElements = this.selectedElements.some((id) => {
        const el = this.getElementById(id);
        return el.required || el.data?.required;
      });

      if (containsRequiredElements) {
        toast.makeToast({
          heading: 'Warning',
          icon: 'warning',
          hideAfter: 4000,
          stack: false,
          text: 'Required elements cannot be copied.',
        });
        return;
      }

      this.clipboard = clone(this.selectedElements);
    },

    pasteFromClipboard() {
      this.clipboard.forEach((id) => {
        const { type, data, name } = this.getElementById(id);
        const shoppableQrcode = type === 'qrcode' && data.providerType === 'shoppable';
        const shoppableProvider = type === 'shoppable';
        const socialDataProvider = type === 'social-data';
        const socialDisplayProvider = type === 'social-display';
        const pollDisplayProvider = type === 'poll-display';

        let containsRestrictedElement = false;
        if (type === 'group') {
          const elements = this.elementGroupMap?.[id] ?? [];
          containsRestrictedElement = elements.some((childId) => {
            const childElement = this.getElementById(childId);
            return ['poll-display', 'social-display'].includes(childElement.type);
          });
        }

        if (!shoppableQrcode && !socialDataProvider && !socialDisplayProvider && !pollDisplayProvider && !shoppableProvider && !containsRestrictedElement) {
          this.duplicateElement({
            value: id,
          });
        } else {
          toast.makeToast({
            heading: 'Information',
            icon: 'info',
            hideAfter: 4000,
            stack: false,
            text: 'Group cannot be duplicated, because it contains a restricted element.',
          });
        }
      });
    },

    undo: debounce(function undo() {
      this.$store.undo();
    }, 200),

    redo: debounce(function redo() {
      this.$store.redo();
    }, 200),
  },
  computed: {
    ...mapState({
      elementGroupMap: (state) => state.graphicsTemplate.elements.elementGroupMap,
    }),

    ...mapGetters('graphicsTemplate', [
      'getElementCounts',
      'getTemplate',
      'getElementById',
    ]),

    postfixedElementCounts() {
      return Object.keys(this.getElementCounts).reduce((acc, key) => {
        acc[`${key}_elements`] = this.getElementCounts[key];
        return acc;
      }, {});
    },

    fontManagerSrc() {
      return `${process.env.TAGLANDIA_URL}/select-asset?owner=${this.owner}&filetype=user-font`;
    },

    owner() {
      return this.template?.owner || this.$store.state.user.owner;
    },

    assetManagerSrc() {
      return `${process.env.TAGLANDIA_URL}/select-asset?owner=${this.owner}&filetype=${this.assetUploaderType}`;
    },

    selectedElements() {
      return this.$store.state.templates.selectedElements;
    },

    template() {
      return this.getTemplate;
    },

    unsaved() {
      return JSON.stringify(this.template) !== JSON.stringify(this.original);
    },

    user() {
      return this.$store.state.user.user;
    },

    textEditing() {
      return this.$store.state.templates.textEditing;
    },
  },
};
